import { useEffect, useState } from 'react';

const getViewportHeight = (): number =>
  window.visualViewport?.height ?? window.innerHeight;

/**
 * Tracks the height of the visual viewport, i.e. the area not covered by the
 * on-screen keyboard. Unlike `window.innerHeight`, `visualViewport.height`
 * reflects the keyboard reliably on both iOS and Android, which makes it the
 * cross-platform signal for reacting to the keyboard showing/hiding.
 *
 * Falls back to `window.innerHeight` (and a `window` resize listener) where
 * `visualViewport` isn't available.
 */
export const useVisualViewportHeight = (): number => {
  const [height, setHeight] = useState(getViewportHeight);

  useEffect(() => {
    const handleResize = (): void => setHeight(getViewportHeight());

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // Sync immediately in case the height changed before this effect ran.
    handleResize();

    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return height;
};
